import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import ButtonComponent from "../button/Button";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import {
  setFinalTreeData,
  setSelectedTreeIds,
  setTransformedTreeData,
  setFilterApplied,
  setBottlerData,
} from "../../../redux/entitySlice";
import CloseIcon from "@mui/icons-material/Close";

// Helper function to find the parent node of a given item ID
function getParentNode(items, id) {
  for (const item of items) {
    if (item.children) {
      if (item.children.some((child) => child.id === id)) {
        return item;
      } else {
        const parentNode = getParentNode(item.children, id);
        if (parentNode) {
          return parentNode;
        }
      }
    }
  }
  return undefined;
}

// Helper function to get all parent IDs of a given item ID
function getAllParentIds(items, id) {
  const parentIds = [];
  let parent = getParentNode(items, id);
  while (parent) {
    parentIds.push(parent.id);
    parent = getParentNode(items, parent.id);
  }
  return parentIds;
}

// Helper function to get selected IDs along with their children
function getSelectedIdsAndChildrenIds(items, selectedIds) {
  const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
  for (const item of items) {
    if (selectedIds.includes(item.id)) {
      selectedIdIncludingChildrenIds.add(item.id);
      if (item.children) {
        const childrenIds = item.children.map((child) => child.id);
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          childrenIds
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId)
        );
      }
    } else if (item.children) {
      const childrenSelectedIds = getSelectedIdsAndChildrenIds(
        item.children,
        selectedIds
      );
      childrenSelectedIds.forEach((selectedId) =>
        selectedIdIncludingChildrenIds.add(selectedId)
      );
    }
  }
  return [...Array.from(selectedIdIncludingChildrenIds)];
}

//Function to determine which IDs should be set based on the current and new selections
function determineIdsToSet(items, newIds, currentIds) {
  const isDeselectingNode = currentIds.length > newIds.length;
  if (isDeselectingNode) {
    const removed = currentIds.filter((id) => !newIds.includes(id))[0];
    const parentIdsToRemove = getAllParentIds(items, removed);
    const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
    const newIdsWithParentsAndChildrenRemoved = newIds.filter(
      (id) => !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id)
    );
    return newIdsWithParentsAndChildrenRemoved;
  }
  const added = newIds.filter((id) => !currentIds.includes(id))[0];
  const idsToSet = getSelectedIdsAndChildrenIds(items, newIds);
  let parent = getParentNode(items, added);
  while (parent) {
    const childIds = parent.children?.map((node) => node.id) ?? [];
    const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));
    if (allChildrenSelected) {
      idsToSet.push(parent.id);
      parent = getParentNode(items, parent.id);
    } else {
      break;
    }
  }
  return idsToSet;
}

const EntitySelector = ({ popupHandler }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [treeData, setTreeData] = useState([]);

  const dispatch = useDispatch();

  const email = useSelector((state) => state.user.email);
  const selectedTreeIds = useSelector((state) => state.entity.selectedTreeIds);
  const transformedTreeDataRedux = useSelector(
    (state) => state.entity.transformedTreeData
  );

  const filterApplied = useSelector((state) => state.entity.filterApplied);

  const transformData = (data) => {
    return data.map((bottler, bottlerIndex) => ({
      id: `bottler${bottlerIndex + 1}`,
      label: bottler.bottler,
      children: bottler.customers.map((customer, customerIndex) => ({
        id: `customer${bottlerIndex + 1}-${customerIndex + 1}`,
        label: `${customer.customerID}`,
        customerID: customer.customerID,
        children: customer.locationIds.map((locationId, locationIndex) => ({
          id: `plant${bottlerIndex + 1}-${customerIndex + 1}-${locationIndex + 1
            }`,
          label: ` ${locationId}`,
          locationId: locationId,
        })),
      })),
    }));
  };

  useEffect(() => {
    if (
      transformedTreeDataRedux === null ||
      transformedTreeDataRedux.length === 0
    ) {
      const postData = async () => {
        try {
          const payload = {
            email: email,
          };
          const response = await ApiService.post(
            "v1/bottler-selection",
            payload
          );

          const bottler = response.data[0]?.bottler;
          dispatch(setBottlerData(bottler));

          const transformedData = transformData(response.data);
          setTreeData(transformedData);

          const allIds = getAllIds(transformedData);
          dispatch(setTransformedTreeData(transformedData));
          dispatch(setSelectedTreeIds(allIds));
          const filteredItems = allIds.filter((item) =>
            item.startsWith("plant")
          );
          const ApplyTreeViewData = filterBottlersByPlant(
            filteredItems,
            transformedData
          );

          dispatch(setFinalTreeData(ApplyTreeViewData));

          //setSelectedIds(allIds);
          
        } catch (error) {
          console.error("Error posting data", error);
        }
      };
      postData();
    } else {
      setTreeData(transformedTreeDataRedux);
      if (filterApplied) {
        setSelectedIds(selectedTreeIds);
      }

    }
  }, [email]);

  const getAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllIds(item.children));
      }
    });
    return ids;
  };

  const filterTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        // If parent matches query, include ALL children (unfiltered)
        // If parent doesn't match, only include children that match
        const children = matches
          ? item.children
          : item.children ? filterTreeData(item.children, query) : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  const filteredData = filterTreeData(treeData, searchQuery);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  //Event handler for when selected items change
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(filteredData, ids, selectedIds);
    setSelectedIds(newSelectedIds);
  };

  // filter bottler by plant
  const filterBottlersByPlant = (plantIds, products) => {
    return products
      .map((bottler) => {
        const filteredCustomers = bottler.children
          .map((customer) => {
            const matchingPlants = customer.children.filter((plant) =>
              plantIds.includes(plant.id)
            );
            return matchingPlants.length > 0
              ? {
                customerId: customer.customerID.split("-")[0], // Extract the part before '-'
                locationId: matchingPlants.map(
                  (plant) => plant.locationId.split("-")[0]
                ), // Extract the part before '-'
              }
              : null;
          })
          .filter(Boolean);

        return filteredCustomers.length > 0
          ? { bottler: bottler.label, customerList: filteredCustomers }
          : null;
      })
      .filter(Boolean)
      .flatMap((bottler) =>
        bottler.customerList.flatMap((customer) =>
          customer.locationId.map((locationId) => ({
            locationId,
            customerId: customer.customerId,
          }))
        )
      );
  };

  //apply selection
  const applySelection = (selectedData) => {
    const filteredItems = selectedData.filter((item) =>
      item.startsWith("plant")
    );

    const ApplyTreeViewData = filterBottlersByPlant(filteredItems, treeData);

    dispatch(setFinalTreeData(ApplyTreeViewData));

    dispatch(setSelectedTreeIds(selectedIds));
    dispatch(setFilterApplied(true));
  };

  // apply and close toggle
  const applyAndClose = () => {
    applySelection(selectedIds);
    popupHandler(false);
  };

  // cancel and close toggle
  const cancelAndClose = () => {
    popupHandler(false);
  };


  return (
    <Box
      sx={{
        width: 340,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #ccc",
        }}
      >
        <Typography variant="h6">Bottler Selection</Typography>
        <IconButton
          onClick={cancelAndClose} // Close the drawer when clicked
          sx={{
            color: "grey",
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, p: 2, overflow: "auto" }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search"
          margin="normal"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        <RichTreeView
          //defaultExpandedItems={["all_plants"]}
          multiSelect
          checkboxSelection
          selectedItems={selectedIds}
          onSelectedItemsChange={handleSelectedItemsChange}
          items={filteredData}
          sx={{
            flexGrow: 1,
            "& .MuiTreeItem-label": {
              fontSize: "11px",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #ccc",
        }}
      >
        <ButtonComponent
          onClick={applyAndClose}
          maxWidth="150px"
          label="Apply"
          bgColor="black"
          color="white"
        />
      </Box>
    </Box>
  );
};

export default EntitySelector;
